import ReactGA from 'react-ga';
import errorToJSON from 'error-to-json';

import {
  ANALYTICS_DIMENSIONS,
  ANALYTICS_EC,
  ANALYTICS_EVENT_CATEGORIES,
  ANALYTICS_EVENTS
} from '../constants/constant';

export class GoogleAnalyticsHelper {

  /**
   * Method that initialises Google Analytics tracking session, setting the `restaurantCode` dimension for the whole session.
   * @returns void
   */
  static initialize(restaurantCode) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }
    ReactGA.initialize(
      __CONFIG__.googleAnalytics.trackingID,
      {
        debug: __CONFIG__.googleAnalytics.debug
      }
    );
    ReactGA.set({[ANALYTICS_DIMENSIONS.RESTAURANT_CODE_DIMENSION]: restaurantCode});
    ReactGA.plugin.require(ANALYTICS_EC.EC);
  }

  /**
   * Method that logs on Google Analytics the current page.
   * @returns void
   */
  static logPageView() {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    ReactGA.pageview(window.location.pathname);
  }

  static logError(description, error) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    ReactGA.ga('send', 'event', ANALYTICS_EVENT_CATEGORIES.JAVASCRIPT_ERROR, `Error - ${description}`, JSON.stringify(errorToJSON(error)), { 'nonInteraction': 1 });
  }

  static identifyMeal(meal) {
    meal.currency = 'GBP';
    ReactGA.ga(`${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_ADD_PRODUCT}`, meal);
  }

  static identifyMeals(meals) {
    meals.forEach((meal) => {
      this.identifyMeal({
        id: meal.mealUuid,
        name: meal.mealData.name,
        price: meal.unitPrice.toString(),
        quantity: meal.quantity,
        category: meal.mealData.categoryCode
      });
    });
  }

  static trackMeal(action, meal, source) {
    if (action === ANALYTICS_EC.EC_ADD_ACTION) {
      this.addMeal(meal, source);
    } else {
      this.removeMeal(meal);
    }
  }

  static addMeal(meal, action = ANALYTICS_EVENTS.ADD_PRODUCT) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

   const type = action === ANALYTICS_EVENT_CATEGORIES.UPSELL ? ANALYTICS_EVENTS.ADD_PRODUCT_FROM_UPSELL : ANALYTICS_EVENTS.ADD_PRODUCT;

    this.identifyMeal(meal);
    ReactGA.ga(`${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`, ANALYTICS_EC.EC_ADD_ACTION);
    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.SHOPPING_CART,
      action: type
    });
  }

  static removeMeal(meal) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    this.identifyMeal(meal);
    ReactGA.ga(`${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`, ANALYTICS_EC.EC_REMOVE_ACTION);

    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.SHOPPING_CART,
      action: ANALYTICS_EVENTS.REMOVE_PRODUCT
    });
  }

  static trackPayment(meals, paymentType) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    this.identifyMeals(meals);

    ReactGA.ga(
      `${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`,
      ANALYTICS_EC.EC_CHECKOUT,
      {
        'step': 2,
        'option': paymentType
      }
    );

    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.SHOPPING_CART,
      action: ANALYTICS_EVENTS.START_PAYMENT
    });
  }

  static trackConfirmation(meals, serviceType) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    this.identifyMeals(meals);

    ReactGA.ga(
      `${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`,
      ANALYTICS_EC.EC_CHECKOUT,
      {
        'step': 1,
        'option': serviceType.replace(/\b\w/g, l => l.toUpperCase())
      }
    );

    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.SHOPPING_CART,
      action: ANALYTICS_EVENTS.CONFIRM_ORDER
    });
  }

  static trackSuccess(meals, purchaseInfo) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    this.identifyMeals(meals);

    purchaseInfo.currency = 'GBP';
    purchaseInfo.tax = '0.00';
    purchaseInfo.affiliation = '';

    ReactGA.ga(
      `${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`,
      ANALYTICS_EC.EC_PURCHASE,
      purchaseInfo
    );

    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.SHOPPING_CART,
      action: ANALYTICS_EVENTS.PURCHASE
    });
  }

  static trackUpSellingClose() {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    ReactGA.ga(`${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`, ANALYTICS_EC.EC_ADD_IMPRESSION);

    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.UPSELL,
      action: ANALYTICS_EVENTS.UPSELL_CLOSE
    });
  }

  static trackUpSellingOpen(meals) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    this.identifyMeal(meals);
    ReactGA.ga(`${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`, ANALYTICS_EC.EC_ADD_IMPRESSION);

    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.UPSELL,
      action: ANALYTICS_EVENTS.UPSELL_OPEN
    });
  }

  static trackUpSellingCheckout(meals, serviceType) {
    if (!__CONFIG__.googleAnalytics.enabled) {
      return;
    }

    this.identifyMeals(meals);

    ReactGA.ga(
      `${ANALYTICS_EC.EC_PREFIX}${ANALYTICS_EC.EC_SET_ACTION}`,
      ANALYTICS_EC.EC_CHECKOUT,
      {
        'step': 1,
        'option': serviceType.replace(/\b\w/g, l => l.toUpperCase())
      }
    );

    ReactGA.event({
      category: ANALYTICS_EVENT_CATEGORIES.UPSELL,
      action: ANALYTICS_EVENTS.UPSELL_CHECKOUT
    });
  }
}



// WEBPACK FOOTER //
// ./helpers/googleAnalytics.helper.js